import { rateFor } from './ccBanks';

// Single source of truth for credit-card pricing conversion.
// Cash is the authored price; every CC plan = cash ÷ factor, rounded UP to RM10.
// Used by the create/edit Price Reference, Section 2, and the frozen quotation
// snapshot so all three tally (latest rates 2026-06-17).
export const CC_FACTORS = { 12: 0.955, 24: 0.935, 36: 0.925, 60: 0.9 };

export const roundUp10 = (v) => Math.ceil((Number(v) || 0) / 10) * 10;

// cash → credit-card price for a term (12/24/36/60). 0 in → 0 out.
export const ccFromCash = (cash, term) => {
    const c = Number(cash) || 0;
    if (c <= 0) return 0;
    return roundUp10(c / (CC_FACTORS[term] || CC_FACTORS[36]));
};

// Payment-method string → conversion factor (1 = cash, no conversion).
export const factorForMethod = (pm) => {
    const s = String(pm || '').toLowerCase();
    return s.includes('60') ? CC_FACTORS[60] : s.includes('36') ? CC_FACTORS[36] : s.includes('24') ? CC_FACTORS[24] : s.includes('12') ? CC_FACTORS[12] : 1;
};

// The factor for a term ON A GIVEN BANK. CC_FACTORS holds one number per term, which
// is right at 12 / 24 / 36 — every bank charges the same there — and only an average
// at 60, where the real rate runs 7.20% (Hong Leong terminal) to 14.00% (HSBC).
//
// This is not only a customer-price question: the commission basis is the converted
// number, so a 60-month sale on HSBC earns on a figure RM 970 below what was actually
// collected, and one on Hong Leong earns on RM 620 more. The bank's own rate settles
// both. (user 2026-10-29: "the % of 60m will follow the rate of the bank aso")
//
// No bank, or a bank that does not offer the term → the flat factor, i.e. exactly the
// behaviour every existing sale was priced and commissioned on.
export const factorFor = (term, bank, bankMethod) => {
    if (!term) return 1;
    if (bank) {
        const rate = rateFor(bank, term, bankMethod || null);
        if (rate != null) return 1 - rate / 100;
    }
    return CC_FACTORS[term] || 1;
};

// Payment-method string → term in months (0 = cash).
export const termForMethod = (pm) => {
    const s = String(pm || '').toLowerCase();
    return s.includes('60') ? 60 : s.includes('36') ? 36 : s.includes('24') ? 24 : s.includes('12') ? 12 : 0;
};

// Is this a MIX (cash + CC) payment method?
export const isMixMethod = (pm) => String(pm || '').toLowerCase().includes('mix');

// The CC-term options MIX can finance its non-cash portion on.
export const MIX_TERM_OPTIONS = [12, 24, 36, 60];

// MIX (cash + CC): the customer pays `cashAmount` in cash; the REST of the deal is financed
// on a CC `term` (÷factor).
//
// CASH PAYS THE ADD-ON FIRST, then whatever is left offsets the system price. It used to be
// the other way round. The add-on is a real thing bought with real money up front — a
// battery, say — so grossing it up by the card factor charges a financing cost on money
// that never went on a card. (user 2026-10-23: "when pay 10k, then need to pay the 7.4k
// add on first with 10k, so add on is not counted as that")
//
// On a 10,000 cash / 7,400 add-on deal that means the add-on stays at 7,400 and only 2,600
// offsets the system. The CUSTOMER TOTAL is unchanged either way — the same cash covers the
// same deal — but the split moves: the add-on shows its true cash price, and the system
// carries the financing it actually bore. The commission base rises with it, because more of
// the commissionable system is genuinely on the card.
//
// Add-ons never earn commission, but they are part of the customer total. Every anchor is
// round-RM10 — same discipline as methodBreakdown.
//   Add-on      = cashᴀ + roundUp10((addOn   − cashᴀ) ÷ factor),  cashᴀ = min(cash, addOn)
//   Commission  = cashₛ + roundUp10((cashNet − cashₛ) ÷ factor),  cashₛ = max(0, cash − addOn)
//   Total       = final + add-on
// uplineDiscount / teamDiscount (2026-08-19): the two new discount buckets. They behave
// EXACTLY like agentDiscount here — off the customer price, off the CC total, and ÷factor
// on a CC/MIX basis. They default to 0 so callers that don't know about them (the RSC and
// CSC calculators — deliberately out of scope) get byte-identical results to before.
// MIX takes the bank the same way methodBreakdown does: only the FINANCED remainder is
// converted, and at 60 months that conversion is the bank's rate, not a flat 10%.
export function mixBreakdown({ originalPrice = 0, asternDiscount = 0, agentDiscount = 0, uplineDiscount = 0, teamDiscount = 0, addOnPrice = 0 }, term, cashAmount = 0, { bank = '', bankMethod = null } = {}) {
    const n = (v) => Number(v) || 0;
    const factor = term ? factorFor(term, bank, bankMethod) : 0;
    const origC = n(originalPrice), astC = n(asternDiscount), agtC = n(agentDiscount), addC = n(addOnPrice);
    const uplC = n(uplineDiscount), teaC = n(teamDiscount);
    const cashNet   = Math.max(0, origC - astC);              // commission base (cash)
    const cashFinal = Math.max(0, cashNet - agtC - uplC - teaC); // customer system price (cash)
    const cashTotal = cashFinal + addC;                       // customer total (cash)
    if (!term || !factor) {                                   // no term → cash passthrough
        return { original: origC, astern: astC, agent: agtC, upline: uplC, team: teaC, addon: addC, final: cashFinal, total: cashTotal, cashPortion: cashTotal, ccPortion: 0 };
    }
    const C = Math.max(0, Math.min(n(cashAmount), cashTotal));
    // Split a cash figure: the part covered by `applied` stays flat, the rest is ÷factor.
    const mix = (cashBase, applied) => {
        const cov = Math.min(applied, cashBase);
        return cov + roundUp10(Math.max(0, cashBase - cov) / factor);
    };
    // The add-on is settled first out of the cash; only the remainder reaches the system.
    const cashToAddon  = Math.min(C, addC);
    const cashToSystem = Math.max(0, C - addC);
    const original = mix(origC, cashToSystem);
    const agent    = Math.round(agtC / factor);
    const upline   = Math.round(uplC / factor);               // same ÷factor as agent
    const team     = Math.round(teaC / factor);               // same ÷factor as agent
    // FINAL is the system price the customer actually pays, converted the SAME way the
    // total is: cash applied first, the remainder divided once. Deriving it instead as
    // `commBase − agent` built it out of two separate round-ups, which is how it could
    // end up ABOVE the total on a sale that is nearly all cash.
    const final    = mix(cashFinal, cashToSystem);
    // Astern is the residual, so Original − Astern − Agent − Upline − Team lands exactly
    // on Final. The backend's commission base is literally OriginalPrice − DiscountByAstern,
    // so this is what keeps commission tallying with the quotation to the ringgit.
    const astern   = Math.max(0, original - final - agent - upline - team);
    // THE CUSTOMER TOTAL IS ONE CONVERSION, NOT A SUM OF ROUNDED PIECES. The whole
    // financed remainder is converted and rounded up to RM10 ONCE — exactly what the
    // quotation does, and exactly the rule methodBreakdown already follows.
    //
    // It used to be `final + addon`, where commBase, addon and agent had each been
    // rounded on their own. Three independent round-ups added together do not equal one
    // round-up of the whole, so the sale page quietly disagreed with the quotation the
    // customer was holding — sale 2266 showed RM 41,580 against a PDF saying RM 41,650,
    // and the payment progress bar tracked the lower figure, so a customer could reach
    // "100% paid" still owing RM 70. (user 2026-10-29, sale 2266)
    const total    = C + roundUp10(Math.max(0, cashTotal - C) / factor);
    // The add-on is the RESIDUAL so the printed rows still add up to that total. This
    // reverses an earlier choice to price the add-on directly: a line that is at most
    // RM10 off its own conversion is a far smaller problem than a total that does not
    // match the quotation, and it is the same trade methodBreakdown makes.
    const addon    = Math.max(0, total - final);
    return { original, astern, agent, upline, team, addon, final, total, cashPortion: Math.min(C, cashTotal), ccPortion: Math.max(0, cashTotal - C) };
}

// THE single canonical Payment Method option list — used IDENTICALLY by the
// create-sales form AND the RSC calculator so the RSC → Create Sales hand-off is a
// direct value copy (no lossy remapping). Keep both dropdowns wired to this.
export const PAYMENT_METHOD_OPTIONS = [
    { label: 'CASH', value: 'CASH' },
    { label: 'CREDIT CARD 12 MONTHS', value: 'creditCard12' },
    { label: 'CREDIT CARD 24 MONTHS', value: 'creditCard24' },
    { label: 'CREDIT CARD 36 MONTHS', value: 'creditCard36' },
    { label: 'CREDIT CARD 60 MONTHS', value: 'creditCard60' },
    { label: 'MIX (CASH + CC)', value: 'mix' },
    // Priced exactly as cash: factorForMethod() returns 1 for any method whose name
    // carries no 12/24/36/60, and the bank pays us in full so there is no card to
    // discount for. (user 2026-10-11: "ther price is same as cash")
    { label: 'BANK LOAN', value: 'LOAN' },
];

// Human label for the selected payment method (printed on the quotation).
export const paymentLabel = (pm) => {
    const s = String(pm || '').toLowerCase();
    if (s.includes('mix')) return 'Mix (Cash + CC)';
    // Checked BEFORE the term, and before the cash fallback. A loan carries no term, so
    // without this line every bank-loan sale printed "Cash" on its own quotation — true
    // of the price, false of how the customer is paying. (user 2026-10-11)
    if (s.includes('loan')) return 'Bank Loan';
    const term = termForMethod(pm);
    return term > 0 ? `Credit Card (${term} Months)` : 'Cash';
};

// Convert a CASH pricing breakdown to a payment-method basis. This is THE single
// rule used by the create/edit Section-2 panel, the frozen quotation snapshot, and
// the commission. Rounding matches the RSC calculator (net ÷ factor, round-up RM10):
//   • Original = roundUp10(cash ÷ factor) — the published CC price-list system price.
//   • FINAL (commission basis) = roundUp10(cash system-net ÷ factor) — the whole net
//     rounded up ONCE, exactly like RSC's ccFromCash. Astern is the RESIDUAL so
//     Original − Astern === Final (and === the backend commBase). One number across the
//     Section-2 Final, the commission, and the quotation (user 2026-07-01).
//   • TOTAL = roundUp10((system-net + add-ons) ÷ factor); add-on = residual (total − final).
// term: 12/24/36/60 (credit card) or 0/falsy (cash, no conversion).
// uplineDiscount / teamDiscount (2026-08-19) ride the agentDiscount rules exactly: they
// come off FinalPrice, off the customer TOTAL, and are ÷factor on a CC basis. Default 0
// so RSC / CSC callers (out of scope, they pass neither) behave exactly as before.
// `bank` (3rd arg) names the card's issuer, with `bankMethod` ('terminal' / 'link')
// only where a bank's rate depends on how the card is taken. Omit both and nothing
// changes — the flat CC_FACTORS apply, exactly as before.
export function methodBreakdown({ originalPrice = 0, asternDiscount = 0, agentDiscount = 0, uplineDiscount = 0, teamDiscount = 0, addOnPrice = 0 }, term, { bank = '', bankMethod = null } = {}) {
    const n = (v) => Number(v) || 0;
    const factor = term ? factorFor(term, bank, bankMethod) : 0;
    if (!term || !factor) {
        const final = Math.max(0, n(originalPrice) - n(asternDiscount) - n(agentDiscount) - n(uplineDiscount) - n(teamDiscount));
        return { original: n(originalPrice), astern: n(asternDiscount), agent: n(agentDiscount), upline: n(uplineDiscount), team: n(teamDiscount), addon: n(addOnPrice), final, total: final + n(addOnPrice) };
    }
    const original = roundUp10(n(originalPrice) / factor);     // CC price-list system price
    const agent = Math.round(n(agentDiscount) / factor);
    const upline = Math.round(n(uplineDiscount) / factor);     // same ÷factor as agent
    const team = Math.round(n(teamDiscount) / factor);         // same ÷factor as agent
    // ONE source of truth (user 2026-07-01): the commission basis is the SAME number the
    // RSC card and the frozen quotation show — round the whole cash system-net UP to RM10
    // ONCE (net ÷ factor), NOT per-component. Astern is then the RESIDUAL so that
    // (original − astern) === this base exactly. That matters because the BACKEND commission
    // base is literally OriginalPrice − DiscountByAstern (SalesCommissionRepository) — so
    // storing the residual astern makes commission tally to the quotation to the ringgit.
    // (Per-component before gave e.g. 30,110 − 4,481 = 25,629 vs the quotation's 25,630.)
    const commBase = roundUp10(Math.max(0, n(originalPrice) - n(asternDiscount)) / factor); // pre-agent, = commission base
    const astern = Math.max(0, original - commBase);
    const final = Math.max(0, commBase - agent - upline - team); // customer-pays / In-house PSC basis
    // TOTAL: round up the whole cash final (incl. agent/upline/team + add-ons) once — the
    // frozen quotation price the customer actually pays.
    const cashTotal = Math.max(0, n(originalPrice) - n(asternDiscount) - n(agentDiscount) - n(uplineDiscount) - n(teamDiscount)) + n(addOnPrice);
    const total = roundUp10(cashTotal / factor);
    const addon = Math.max(0, total - final);                  // residual → rows sum to total
    return { original, astern, agent, upline, team, addon, final, total };
}
